import React, { useEffect } from 'react'
import styled from 'styled-components'
import { Box, Flex, getPaletteColor } from 'pcln-design-system'
import analytics from '@/shared-utils/analytics'
import { ANALYTICS_CATEGORY_HOME } from '@/constants/analytics'
import { Booking } from './types'
import { buildContent } from './utils'
import useCrossSellInventory from './useCrossSellInventory'
import Inventory from './Inventory'
import NoInventoryFallback from './NoInventoryFallback'
import BookingInfo from './BookingInfo'

const Wrapper = styled(Box)`
  border-top: 4px solid ${getPaletteColor('primary.base')};
`

/**
 * This is needed because of the following scenarios
 *
 * view more button with inventory - example text is "view more rental car deals" - product is singular and lowercase
 * search button with no inventory - example text is "Search Rental Cars" - product is upper case and plural
 */

const X_SELL_PRODUCTS = {
  'Rental Cars': 'rental car',
  Hotels: 'hotel',
  Flights: 'flight'
} as const

export default function CrossSell({ booking }: { booking: Booking }) {
  const {
    product,
    date,
    headline,
    offerToken,
    subHeadline,
    xSellMessaging,
    xSellTarget,
    xSellUrl
  } = buildContent(booking)

  const inventory = useCrossSellInventory(booking)
  const { listings, offerNum } = inventory
  const xSellProduct = X_SELL_PRODUCTS[xSellTarget]

  useEffect(() => {
    analytics.fireEvent({
      action: 'xsell load',
      category: ANALYTICS_CATEGORY_HOME,
      label: 'xsell display'
    })
  }, [])

  useEffect(() => {
    if (listings.length) {
      analytics.fireEvent({
        action: 'upcoming trip',
        category: ANALYTICS_CATEGORY_HOME,
        label: 'xsell inventory display'
      })
    }
  }, [listings.length])

  return (
    <Wrapper
      className="cross-sell-container"
      mx={0}
      my={[3, 0]}
      color="background.lightest"
      boxShadowSize="md"
      borderRadius="xl"
    >
      <Flex flexDirection={['column', null, 'row']}>
        <BookingInfo
          product={product}
          date={date}
          headline={headline}
          subHeadline={subHeadline}
        />
        {listings.length ? (
          <Inventory
            listings={listings}
            offerNum={offerNum}
            xSellMessaging={xSellMessaging}
            xSellProduct={xSellProduct}
            rudderParentProductCode={product}
            rudderChildProductCode={xSellProduct}
            xSellUrl={xSellUrl}
            offerToken={offerToken}
          />
        ) : (
          <NoInventoryFallback
            xSellMessaging={xSellMessaging}
            xSellTarget={xSellTarget}
            xSellUrl={xSellUrl}
            rudderParentProductCode={product}
            rudderChildProductCode={xSellProduct}
            offerNum={offerNum}
          />
        )}
      </Flex>
    </Wrapper>
  )
}
